import { FormInputSearch } from '@systemeio/ui'
import TabMenu from 'modules/calendar/components/tab-menu'
import { calendarLoco } from 'modules/calendar/constants/calendar-loco'
import { removeEvent } from 'modules/calendar/events/api/events-api'
import { EventCreateModal } from 'modules/calendar/events/components/event-create-modal'
import { eventTypeLocoKeys } from 'modules/calendar/events/enums/event-type-enum'
import { useEventLimitReached } from 'modules/calendar/events/hooks/use-event-limit-reached'
import { useEvents } from 'modules/calendar/events/hooks/use-events'
import { EventFilterInterface } from 'modules/calendar/events/types/calendar-filter-interface'
import { EventListInterface } from 'modules/calendar/events/types/new-event-interface'
import { NextPage } from 'next'
import React from 'react'
import { TIME_LABELS, TimeType } from 'shared/components/date-picker/time-delay-picker/constants'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'

const defaultFilters: EventFilterInterface = { name: '' }

const Calendar: NextPage = () => {
  const { t, tICU } = useLocoTranslation()

  const { setPagination, pagination, setLimit, limit, filter, setFilter } =
    useFilterPagination<EventFilterInterface>(defaultFilters)

  const { events, counter } = useEvents({ limit, pagination, filter })
  const { eventLimit, isFetchingEventLimit } = useEventLimitReached()

  const { revalidateModules } = useRevalidateModules()

  const columns: ColumnDefinitionInterface<EventListInterface>[] = [
    {
      key: 'name',
      header: t('dashboard.calendar.event_name'),
      widthPercent: 60,
      onRender: event => (
        <div className="truncate">
          <LinkWithoutPrefetch
            className="text-brand-primary hover:text-text-secondary ring-brand-primary rounded-xs w-full outline-hidden ring-offset-1 focus-visible:ring-2"
            href={`/calendar/${event.id}?eventType=${event.eventType}`}
          >
            {event.name}
          </LinkWithoutPrefetch>
        </div>
      ),
    },
    {
      key: 'eventDuration',
      header: t('dashboard.calendar.event_duration'),
      widthPercent: 40,
      onRender: event => (
        <span>
          {event.eventDuration}{' '}
          {tICU(TIME_LABELS[TimeType.MINUTES], { count: event.eventDuration })}
        </span>
      ),
    },
    {
      key: 'eventType',
      header: t('dashboard.calendar.event_type'),
      widthPercent: 30,
      onRender: event => <span>{t(eventTypeLocoKeys[event.eventType])}</span>,
    },
  ]

  const onRemoveEvent = async (event: EventListInterface) => {
    await removeEvent(event.id)
    await revalidateModules([RequestModuleEnum.Calendar, RequestModuleEnum.CalendarBookings], true)
  }

  return (
    <>
      <TabMenu />
      <ListingLayout
        data={events}
        title={t('dashboard.calendar.title_events')}
        customHeaderElement={
          <EventCreateModal
            isFetchingEventLimit={isFetchingEventLimit}
            limitReached={eventLimit?.limitReached}
          />
        }
        type={'compact'}
        counter={counter}
        filterData={{
          filterer: { filter, setFilter, defaultFilters },
          paginator: { pagination, setPagination },
          limiter: { limit, setLimit },
          testAttributePostfix: 'filter-btn',
          filterable: [
            {
              field: 'name',
              onRender: (value, onChange) => (
                <FormInputSearch
                  key={'events-search-filter'}
                  onClear={() => onChange('')}
                  value={value}
                  onChange={e => onChange(e.target.value)}
                  testAttributePostfix={'search-input'}
                />
              ),
            },
          ],
        }}
      >
        <Table
          caption={t('dashboard.calendar.title_event')}
          data={events}
          columns={columns}
          limiter={{ limit, setLimit }}
          paginator={{ pagination, setPagination }}
          actions={{
            edit: {
              setHref: event => `calendar/${event.id}?eventType=${event.eventType}`,
              isNext: true,
            },
            remove: {
              onChange: event => onRemoveEvent(event),
              confirmationText: event =>
                event.hasActiveBookings
                  ? t('dashboard.calendar.remove_paid_event')
                  : t('dashboard.calendar.remove_free_event'),
              confirmationTitle: event =>
                event.hasActiveBookings && t('dashboard.calendar.remove_paid_event_title'),
            },
          }}
        />
      </ListingLayout>
    </>
  )
}

export const getServerSideProps = getBaseServerSideProps(calendarLoco, 'header.top_menu.events')

export default Calendar
